import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from './PageHeader';
import { apiClient } from '../services/api';
import { useLanguage, useTranslation } from '../i18n';
import type { Friend, FriendshipRequest, UserSearchResult } from '../types';
import './FriendsPage.css';

export const FriendsPage: React.FC = () => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<FriendshipRequest[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [processingRequests, setProcessingRequests] = useState<Set<number>>(new Set());
  const [sendingRequests, setSendingRequests] = useState<Set<number>>(new Set());
  const { language } = useLanguage();
  const t = useTranslation();

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [friendsData, incomingData] = await Promise.all([
        apiClient.getFriends(),
        apiClient.getIncomingFriendRequests(),
      ]);
      setFriends(friendsData);
      setIncomingRequests(incomingData);
    } catch (error) {
      console.error('Error loading friends data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await apiClient.searchUsers(searchQuery);
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleAcceptRequest = async (requestId: number) => {
    if (processingRequests.has(requestId)) return;
    
    setProcessingRequests(prev => new Set(prev).add(requestId));
    try {
      await apiClient.acceptFriendRequest(requestId);
      // Refresh data
      await loadData();
      // Haptic feedback
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Error accepting request:', error);
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setProcessingRequests(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    if (processingRequests.has(requestId)) return;
    
    setProcessingRequests(prev => new Set(prev).add(requestId));
    try {
      await apiClient.rejectFriendRequest(requestId);
      // Remove from list
      setIncomingRequests(prev => prev.filter(r => r.id !== requestId));
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
    } finally {
      setProcessingRequests(prev => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
    }
  };

  const handleSendRequest = async (userId: number) => {
    if (sendingRequests.has(userId)) return;
    
    setSendingRequests(prev => new Set(prev).add(userId));
    try {
      await apiClient.sendFriendRequest({ user_id: userId });
      // Update search results to reflect the new status
      setSearchResults(prev => prev.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            friendship_status: {
              status: 'pending',
              can_send_request: false,
              is_requester: true,
            },
          };
        }
        return user;
      }));
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setSendingRequests(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const getLocale = () => {
    switch (language) {
      
      
      default: return 'es-ES';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return t.common.today;
    } else if (diffDays === 1) {
      return t.common.yesterday;
    } else if (diffDays < 7) {
      return `${diffDays} ${t.common.daysAgo}`;
    } else {
      return date.toLocaleDateString(getLocale(), { day: 'numeric', month: 'short' });
    }
  };

  const getUserDisplayName = (user: Friend | undefined): string => {
    if (!user) return t.friendsPage.user;
    return user.first_name + (user.last_name ? ` ${user.last_name}` : '');
  };

  const getUserInitial = (user: Friend | undefined): string => {
    if (!user || !user.first_name) return '?';
    return user.first_name.charAt(0).toUpperCase();
  };

  const formatLastActivity = (dateString: string | null | undefined): string => {
    if (!dateString) return t.friendsPage.noPractices;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return t.common.today;
    } else if (diffDays === 1) {
      return t.common.yesterday;
    } else if (diffDays < 7) {
      return `${diffDays} ${t.common.daysAgo}`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${t.common.weeksAgo}`;
    } else {
      return date.toLocaleDateString(getLocale(), { day: 'numeric', month: 'short' });
    }
  };

  const renderFriendshipButton = (user: UserSearchResult) => {
    const { friendship_status } = user;
    const isSending = sendingRequests.has(user.id);

    if (friendship_status.status === 'accepted') {
      return <span className="status-badge friends">{t.friendsPage.alreadyFriends}</span>;
    }

    if (friendship_status.status === 'pending') {
      if (friendship_status.is_requester) {
        return <span className="status-badge pending">{t.friendsPage.requestSent}</span>;
      } else {
        return <span className="status-badge pending-incoming">{t.friendsPage.wantsToAdd}</span>;
      }
    }

    if (friendship_status.can_send_request) {
      return (
        <button
          className="add-friend-btn"
          onClick={() => handleSendRequest(user.id)}
          disabled={isSending}
        >
          {isSending ? t.friendsPage.sending : t.friendsPage.add}
        </button>
      );
    }

    return null;
  };

  return (
    <div className="page-container friends-page">
      <PageHeader title={t.friendsPage.title} />

      <div className="friends-content">
        {/* Search section */}
        <div className="search-section">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder={t.friendsPage.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Search results */}
        {searchQuery.length >= 2 && (
          <div className="search-results-section">
            <h2 className="section-title">{t.friendsPage.searchResults}</h2>
            {isSearching ? (
              <div className="loading-state">{t.friendsPage.searching}</div>
            ) : searchResults.length > 0 ? (
              <div className="users-list">
                {searchResults.map((user) => (
                  <div key={user.id} className="user-card">
                    <div className="user-avatar">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt={getUserDisplayName(user)} />
                      ) : (
                        <div className="avatar-placeholder">
                          {getUserInitial(user)}
                        </div>
                      )}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{getUserDisplayName(user)}</div>
                      {user.username && (
                        <div className="user-username">@{user.username}</div>
                      )}
                    </div>
                    <div className="user-action">
                      {renderFriendshipButton(user)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <div className="empty-text">{t.friendsPage.noResults}</div>
              </div>
            )}
          </div>
        )}

        {/* Incoming requests */}
        {incomingRequests.length > 0 && (
          <div className="requests-section">
            <h2 className="section-title">
              {t.friendsPage.friendRequests}
              <span className="requests-count">{incomingRequests.length}</span>
            </h2>
            <div className="requests-list">
              {incomingRequests.map((request) => (
                <div key={request.id} className="request-card">
                  <div className="user-avatar">
                    {request.requester?.avatar_url ? (
                      <img src={request.requester.avatar_url} alt={getUserDisplayName(request.requester)} />
                    ) : (
                      <div className="avatar-placeholder">
                        {getUserInitial(request.requester)}
                      </div>
                    )}
                  </div>
                  <div className="user-info">
                    <div className="user-name">{getUserDisplayName(request.requester)}</div>
                    {request.requester?.username && (
                      <div className="user-username">@{request.requester.username}</div>
                    )}
                    <div className="request-date">{formatDate(request.created_at)}</div>
                  </div>
                  <div className="request-actions">
                    <button
                      className="accept-btn"
                      onClick={() => handleAcceptRequest(request.id)}
                      disabled={processingRequests.has(request.id)}
                    >
                      ✓
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => handleRejectRequest(request.id)}
                      disabled={processingRequests.has(request.id)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Friends list */}
        <div className="friends-list-section">
          <h2 className="section-title">
            {t.friendsPage.myFriends}
            {friends.length > 0 && (
              <span className="friends-count">{friends.length}</span>
            )}
          </h2>
          
          {isLoading ? (
            <div className="loading-state">{t.common.loading}</div>
          ) : friends.length > 0 ? (
            <div className="friends-list">
              {friends.map((friend) => (
                <div key={friend.id} className="friend-card">
                  <div className="friend-card-header">
                    <div className="user-avatar">
                      {friend.avatar_url ? (
                        <img src={friend.avatar_url} alt={getUserDisplayName(friend)} />
                      ) : (
                        <div className="avatar-placeholder">
                          {getUserInitial(friend)}
                        </div>
                      )}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{getUserDisplayName(friend)}</div>
                      {friend.username && (
                        <div className="user-username">@{friend.username}</div>
                      )}
                    </div>
                  </div>
                  {friend.stats && (
                    <div className="friend-stats">
                      <div className="friend-stats-row">
                        <div className="friend-stat">
                          <span className="stat-icon">🔥</span>
                          <span className="stat-value">{friend.stats.streak_days}</span>
                          <span className="stat-label">{t.common.days}</span>
                        </div>
                        <div className="friend-stat">
                          <span className="stat-icon">🧘</span>
                          <span className="stat-value">{friend.stats.meditations_completed}</span>
                          <span className="stat-label">{t.friendsPage.medit}</span>
                        </div>
                        <div className="friend-stat">
                          <span className="stat-icon">✅</span>
                          <span className="stat-value">{friend.stats.tasks_completed}</span>
                          <span className="stat-label">{t.friendsPage.tasksShort}</span>
                        </div>
                      </div>
                      <div className="friend-last-activity">
                        {t.friendsPage.lastPractice} {formatLastActivity(friend.stats.last_meditation_at)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <div className="empty-text">{t.friendsPage.noFriendsYet}</div>
              <div className="empty-hint">{t.friendsPage.findFriends}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
